<template>
  <v-fade-transition hide-on-leave>
    <v-layout column class="overflow-y-hidden" id="nx-bag-clipboard">
      <div class="nx-bag__container">
        <v-layout row wrap class="nx-bag-clipboard-tabs" v-if="hasPublicClipboard">
          <v-btn :class="{ selected: isPrivateClipboard }"
                 @click="changePrivate"
                 :loading="loading.private"
                 block dark>{{ $t('message.bag.clipboard.tabs.private') }}</v-btn>
          <v-btn :class="{ selected: isPublicClipboard }"
                 @click="changePublic"
                 :loading="loading.public"
                 block dark> {{ $t('message.bag.clipboard.tabs.public') }}</v-btn>
        </v-layout>
          <template v-if="items.length > 0">
            <nx-bag-clipboard-list :items="items"
                                   :public-clipboard="isPublicClipboard"
                                   @click:item="selectItem"
                                   @click:clone="cloneItem"
                                   @click:move="moveItem"
                                   @click:multisite="multisiteItem"
                                   @click:delete="remove" />
          </template>
          <nx-bag-empty-item v-else :text="$t('message.bag.clipboard.empty')"></nx-bag-empty-item>
      </div>
    </v-layout>
  </v-fade-transition>
</template>

<script>
import { registerEventHandler, removeEventHandler } from '@/overlord/events';
import { mapActions, mapGetters, mapState } from 'vuex';
import { PUBLIC_CLIPBOARD, PRIVATE_CLIPBOARD, PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT } from '@/modules/bag/store/clipboard';
import NxBagEmptyItem from './components/common/BagEmptyItem';
import NxBagClipboardList from '@/modules/bag/components/clipboard/BagClipboardList';

export default {
  components: {
    NxBagClipboardList,
    NxBagEmptyItem,
  },
  data() {
    return {
      loading: {
        public: false,
        private: false,
      },
    };
  },
  computed: {
    ...mapGetters('bag/clipboard', ['id', 'items', 'current', 'privateClipboardId', 'publicClipboardId']),
    isPublicClipboard() {
      return this.current === PUBLIC_CLIPBOARD;
    },
    isPrivateClipboard() {
      return this.current === PRIVATE_CLIPBOARD;
    },
    hasPublicClipboard() {
      return this.publicClipboardId;
    },
  },
  methods: {
    ...mapActions('bag/clipboard', {
      remove: 'REMOVE',
      rename: 'RENAME',
      cloneItem: 'CLONE',
      loadPublicClipboard: 'LOAD_PUBLIC',
      fetchPublicClipboard: 'FETCH_PUBLIC',
      setPublicClipboard: 'SET_PUBLIC',
      reloadPublicClipboard: 'RELOAD_PUBLIC',
      loadPrivateClipboard: 'LOAD_PRIVATE',
      moveItemToPublicClipboard: 'MOVE_ITEM_TO_PUBLIC',
      convertToMultisite: 'CONVERT_ITEM_TO_MULTISITE',
    }),
    ...mapActions('bag', {
      selectItem: 'ITEM_SELECTED',
    }),
    ...mapState('bag/clipboard', ['currentSiteAlias']),
    async changePublic() {
      if (this.isPublicClipboard || this.loading.public || this.loading.private) return;
      try {
        this.loading.public = true;
        await this.loadPublicClipboard(this.publicClipboardId);
      } catch (e) {
        console.log(e);
        this.toast.notify('message.error.serverError');
      } finally {
        this.loading.public = false;
      }
    },
    async changePrivate() {
      if (this.isPrivateClipboard || this.loading.public || this.loading.private) return;
      try {
        this.loading.private = true;
        await this.loadPrivateClipboard(this.privateClipboardId);
      } catch (e) {
        console.log(e);
        this.toast.notify('message.error.serverError');
      } finally {
        this.loading.private = false;
      }
    },
    async moveItem(item) {
      this.moveItemToPublicClipboard(item);
    },
    async multisiteItem(item) {
      this.convertToMultisite({ item, idAlias: this.$store.state.site.idAlias });
    },
    handlePublicItemsUpdateEvent({ payload }) {
      if (payload.items && Array.isArray(payload.items)) {
        if (payload.idAlias === this.currentSiteAlias) {
          // If updated public was triggered from same site, just set public clipboard
          this.setPublicClipboard(payload.items);
        } else {
          // If updated public was trigger from different site, force reload
          this.reloadPublicClipboard(payload.items);
        }
      }
    },
  },
  watch: {
    publicClipboardId: {
      handler(id) {
        // Preload site clipboard
        if (id) this.fetchPublicClipboard(id);
      },
      immediate: true,
    },
  },
  mounted() {
    this.eventHandler = registerEventHandler(PUBLIC_CLIPBOARD_ITEMS_UPDATED_EVENT, this.handlePublicItemsUpdateEvent);
  },
  beforeDestroy() {
    removeEventHandler(this.eventHandler);
  },
};
</script>

<style lang="stylus">
@import '../../assets/stylus/variables.styl';

#nx-bag-clipboard {
  .nx-bag-clipboard-tabs {
    padding: 12px 20px;
    box-shadow: inset 0 -1px 0 0 black;
    .v-btn {
      height: 28px;
      background-color: $nx-primary--darken-2;
      &__content {
        font-size: 15px;
        font-weight: 500;
        font-stretch: normal;
        font-style: normal;
        line-height: 1;
        letter-spacing: 0.19px;
        text-align: center;
        color: white;
        text-transform: capitalize;
      }

      &.selected {
        background-color: $nx-primary--lighten-5;
        .v-btn__content {
          color: $nx-primary--darken-3;
        }
      }
    }
  }
}
</style>
